"use client";

import React, { useEffect, useRef, useState } from "react";
import { CircleCheck, Quote } from "lucide-react";
import { Separator } from "@/components/ui/separator";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const benefits = ["Altersflecken auf dem Handrücken", "Faltigen, ungepflegt wirkenden Händen", "Verbesserung des Hautbildes Ihrer Hände mit Sofort-Effekt"];
export default function BeweiHandsTreatment() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: benefitsRef,
    isVisible: isBenefitsVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: techRef,
    isVisible: isTechVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: founderRef,
    isVisible: isFounderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: doctorRef,
    isVisible: isDoctorVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section id="beweihandstreatment" className="bg-background text-foreground overflow-hidden">
      <div className="flex flex-col gap-24 md:gap-32">
        
        {}
        <div ref={headerRef} className={`w-full transition-all duration-1000 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="relative w-full min-h-[500px] md:min-h-[700px] flex flex-col items-center justify-center gap-2 overflow-hidden pb-24 px-6">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1642411/img/bewei_hands_hero_banner.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1642411/img/bewei_hands_hero_banner.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1642411/img/bewei_hands_hero_banner.png-wvc-sizes" alt="BEWEI Hands Treatment Hero" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-black/40 z-10"></div>
            <div className="relative z-20 flex flex-col items-center text-center">
              <h2 className="font-default font-bold tracking-widest uppercase !text-6xl !text-[rgba(160,255,241,1)] md:!text-8xl">Hands UP</h2>
              <span className="text-xl md:text-2xl font-default tracking-[0.3em] text-white uppercase">BEWEI Hands lässt ihre Hände strahlen&nbsp;</span>
            </div>
          </div>
        </div>

        {}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col gap-24 md:gap-32 w-full">
          
          <div ref={benefitsRef} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center transition-all duration-1000 delay-200 transform ${isBenefitsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative aspect-[4/5] lg:aspect-square rounded-2xl overflow-hidden shadow-2xl">
              <img src="https://stable-sturgeon.10web.cloud/wp-content/uploads/2026/04/bewei_hands_byLinda.jpg" data-wvc-srcset="https://stable-sturgeon.10web.cloud/wp-content/uploads/bewei_hands_hero.png-wvc-srcset" data-wvc-sizes="https://stable-sturgeon.10web.cloud/wp-content/uploads/bewei_hands_hero.png-wvc-sizes" alt="Frau berührt sanft ihr Gesicht mit gepflegten Händen" className="object-cover w-full h-full" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent mix-blend-overlay"></div>
            </div>
            
            <div className="flex flex-col gap-8">
              <h3 className="font-default uppercase tracking-wider leading-snug !text-[rgba(21,153,123,1)] !text-4xl">
                Kann unterstützend wirken bei
              </h3>
              <Separator className="w-24 bg-primary/30" />
              <ul className="flex flex-col gap-6">
                {benefits.map((benefit, i) => <li key={i} data-index={i} className="flex items-start gap-4">
                    <CircleCheck className="w-6 h-6 text-primary shrink-0 mt-1" />
                    <span className="text-lg text-muted-foreground leading-relaxed">
                      {benefit}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>

          <div ref={techRef} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center transition-all duration-1000 transform ${isTechVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="flex flex-col gap-8 order-2 lg:order-1">
              <h3 className="font-default uppercase tracking-wider leading-snug !text-[rgba(16,147,138,1)] !text-4xl">
                Für junge Hände, die das Alter nicht verraten.
              </h3>
              <Separator className="w-24 bg-primary/30" />
              <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                <p>
                  BEWEI HANDS ist das weltweit erste Gerät zur professionellen automatisierten Behandlung für jünger und schöner wirkende Hände.
                </p>
                <p>
                  Sanfte elektromagnetische Wellen. Kein Entkleiden, keine Schmerzen. Die Wirkung setzt sofort ein. Es genügt, einfach die Hände in die dafür vorgesehene Kammer zu legen.
                </p>
                <p>
                  Dauer 10 Minuten pro Hand - schonend und sanft zur Haut. Die seit 40 Jahren etablierte und einzigartige Technologie von BEWEI HANDS kommt ursprünglich aus der Arthrosetherapie.
                </p>
              </div>
            </div>
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-xl order-1 lg:order-2">
              <img src="https://stable-sturgeon.10web.cloud/wp-content/uploads/2026/05/still-1-0627c586.jpg" data-wvc-srcset="https://stable-sturgeon.10web.cloud/wp-content/uploads/bewei_hands_device_use.png-wvc-srcset" data-wvc-sizes="https://stable-sturgeon.10web.cloud/wp-content/uploads/bewei_hands_device_use.png-wvc-sizes" alt="BEWEI HANDS Gerät in Anwendung" className="object-cover w-full h-full" loading="lazy" />
            </div>
          </div>

          <div ref={founderRef} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center transition-all duration-1000 transform ${isFounderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-xl">
              <img src="https://stable-sturgeon.10web.cloud/wp-content/uploads/2026/05/haende-e0871cdc.jpg" data-wvc-srcset="https://stable-sturgeon.10web.cloud/wp-content/uploads/bewei_hands_before_after.png-wvc-srcset" data-wvc-sizes="https://stable-sturgeon.10web.cloud/wp-content/uploads/bewei_hands_before_after.png-wvc-sizes" alt="Vorher-Nachher Vergleich der Hände" className="object-cover w-full h-full" loading="lazy" />
            </div>
            <div className="flex flex-col gap-8 bg-secondary/50 p-8 md:p-12 rounded-3xl relative">
              <Quote className="absolute top-6 left-6 w-12 h-12 text-primary/10 rotate-180" />
              <blockquote className="text-xl md:text-2xl italic font-light leading-relaxed text-foreground/90 relative z-10 mt-4">
                „Ich führe seit mehr als zwei Jahren einen Selbstversuch durch und behandle NUR meine linke Hand (mit dem Ring) – das Ergebnis sehen Sie hier. Zudem spüre ich eine deutlich bessere Beweglichkeit in der behandelten Hand."
              </blockquote>
              <div className="flex flex-col">
                <span className="font-default font-semibold text-primary">Marion Jörgens</span>
                <span className="text-sm text-muted-foreground">Gründerin BEWEI</span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}